import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  PiggyBank, 
  Wallet, 
  CreditCard,
  Shield,
  BarChart3,
  ArrowRight,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  Zap,
  Target,
  AlertTriangle,
  CheckCircle,
  Building,
  RefreshCw,
  AlertCircle,
  Heart,
  Repeat,
  ArrowUpRight,
  ArrowDownRight,
  Minus
} from "lucide-react";

/**
 * Alternative Money Cycle - Card-Based Dashboard
 * 
 * Three Core Scenarios:
 * 1. WEALTH CREATION LOOP - Income > Expenses (surplus > 4%)
 *    - Can save money, invest, create wealth
 * 
 * 2. LIVING DAY TO DAY - Income ≈ Expenses (difference < 4%)
 *    - No wealth generation, vulnerable to contingencies
 * 
 * 3. POVERTY/BANKRUPTCY LOOP - Expenses > Income
 *    - Needs debt or external support to survive
 */

const formatCurrency = (amount, currency = "USD") => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// Determine which financial cycle/loop the user is in
const getFinancialCycle = (income, expenses, t) => {
  if (income === 0 && expenses === 0) {
    return {
      type: 'no-data',
      name: t('moneyCycle.noData'),
      description: t('moneyCycle.noDataDescription'),
      color: '#6B7280',
      bgGradient: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)',
      icon: Target
    };
  }

  const surplus = income - expenses;
  const surplusRate = income > 0 ? (surplus / income) * 100 : -100;

  if (surplusRate > 4) {
    return {
      type: 'wealth-creation',
      name: t('moneyCycle.wealthCreation'),
      description: t('moneyCycle.wealthCreationDesc'),
      longDescription: t('moneyCycle.wealthCreationLong'),
      color: '#10B981',
      bgGradient: 'linear-gradient(135deg, #065F46 0%, #047857 50%, #059669 100%)',
      lightBg: '#D1FAE5',
      icon: TrendingUp,
      loopIcon: ArrowUpRight,
      advice: [
        t('moneyCycle.buildEmergencyFund'),
        t('moneyCycle.investSurplus'),
        t('moneyCycle.increaseRetirement'),
        t('moneyCycle.protectWealth')
      ]
    };
  } else if (surplusRate >= -4 && surplusRate <= 4) {
    return {
      type: 'day-to-day',
      name: t('moneyCycle.dayToDay'),
      description: t('moneyCycle.dayToDayDesc'),
      longDescription: t('moneyCycle.dayToDayLong'),
      color: '#F59E0B',
      bgGradient: 'linear-gradient(135deg, #92400E 0%, #B45309 50%, #D97706 100%)',
      lightBg: '#FEF3C7',
      icon: Minus,
      loopIcon: RefreshCw,
      advice: [
        t('moneyCycle.reduceExpenses'),
        t('moneyCycle.increaseIncome'),
        t('moneyCycle.startEmergencyFund'),
        t('moneyCycle.avoidNewDebt')
      ]
    };
  } else {
    return {
      type: 'poverty-loop',
      name: t('moneyCycle.povertyLoop'),
      description: t('moneyCycle.povertyLoopDesc'),
      longDescription: t('moneyCycle.povertyLoopLong'),
      color: '#EF4444',
      bgGradient: 'linear-gradient(135deg, #7F1D1D 0%, #991B1B 50%, #B91C1C 100%)',
      lightBg: '#FEE2E2',
      icon: TrendingDown,
      loopIcon: ArrowDownRight,
      advice: [
        t('moneyCycle.cutNonEssential'),
        t('moneyCycle.seekAdditionalIncome'),
        t('moneyCycle.debtConsolidation'),
        t('moneyCycle.reachOutForHelp')
      ]
    };
  }
};

// Circular Progress Ring Component
const ProgressRing = ({ progress, size = 120, strokeWidth = 10, color = "#10B981", bgColor = "#E5E7EB", children }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const clampedProgress = Math.max(0, Math.min(100, progress));
  const offset = circumference - (clampedProgress / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={bgColor}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {children}
      </div>
    </div>
  );
};

// Cycle Visualization Component
const CycleVisualization = ({ cycle, income, expenses, surplus }) => {
  const LoopIcon = cycle.loopIcon || RefreshCw;
  
  return (
    <div 
      className="rounded-3xl p-6 text-white shadow-2xl overflow-hidden relative"
      style={{ background: cycle.bgGradient }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-64 h-64 transform translate-x-32 -translate-y-32">
          <LoopIcon className="w-full h-full" />
        </div>
      </div>

      <div className="relative z-10">
        {/* Cycle Name Badge */}
        <div className="flex items-center justify-center mb-4">
          <div 
            className="px-4 py-2 rounded-full flex items-center gap-2 text-sm font-bold"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
          >
            <LoopIcon className="w-5 h-5" />
            {cycle.name.toUpperCase()}
          </div>
        </div>

        {/* Main Stats */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Progress Ring */}
          <div className="flex flex-col items-center">
            <ProgressRing 
              progress={income > 0 ? Math.max(0, ((income - expenses) / income) * 100 + 50) : 50}
              size={160}
              strokeWidth={14}
              color="rgba(255,255,255,0.9)"
              bgColor="rgba(255,255,255,0.2)"
            >
              <div className="text-center">
                <p className="text-xs text-white/70 uppercase tracking-wider">Net Flow</p>
                <p className={`text-2xl font-bold ${surplus >= 0 ? 'text-white' : 'text-white'}`}>
                  {surplus >= 0 ? '+' : ''}{formatCurrency(surplus)}
                </p>
                <p className="text-xs text-white/70">per month</p>
              </div>
            </ProgressRing>
          </div>

          {/* Income & Expenses Cards */}
          <div className="flex-1 grid grid-cols-2 gap-4 w-full md:w-auto">
            <div className="bg-white/15 backdrop-blur rounded-2xl p-4 text-center border border-white/20">
              <TrendingUp className="w-6 h-6 text-emerald-300 mx-auto mb-2" />
              <p className="text-xs text-white/70 uppercase tracking-wider">Income</p>
              <p className="text-xl md:text-2xl font-bold text-white">{formatCurrency(income)}</p>
              <p className="text-xs text-white/50 mt-1">per month</p>
            </div>
            <div className="bg-white/15 backdrop-blur rounded-2xl p-4 text-center border border-white/20">
              <TrendingDown className="w-6 h-6 text-red-300 mx-auto mb-2" />
              <p className="text-xs text-white/70 uppercase tracking-wider">Expenses</p>
              <p className="text-xl md:text-2xl font-bold text-white">{formatCurrency(expenses)}</p>
              <p className="text-xs text-white/50 mt-1">per month</p>
            </div>
          </div>
        </div>

        {/* Cycle Description */}
        <div className="mt-6 p-4 bg-white/10 backdrop-blur rounded-xl border border-white/20">
          <p className="text-white/90 text-sm md:text-base leading-relaxed">
            {cycle.description}
          </p>
        </div>

        {/* Visual Loop Indicator */}
        <div className="flex justify-center mt-6">
          {cycle.type === 'wealth-creation' && (
            <div className="flex items-center gap-2 text-emerald-300">
              <Wallet className="w-5 h-5" />
              <ArrowRight className="w-4 h-4" />
              <PiggyBank className="w-5 h-5" />
              <ArrowRight className="w-4 h-4" />
              <BarChart3 className="w-5 h-5" />
              <ArrowRight className="w-4 h-4" />
              <TrendingUp className="w-5 h-5" />
              <RefreshCw className="w-4 h-4 ml-2 animate-spin" style={{ animationDuration: '3s' }} />
            </div>
          )}
          {cycle.type === 'day-to-day' && (
            <div className="flex items-center gap-2 text-amber-300">
              <Wallet className="w-5 h-5" />
              <ArrowRight className="w-4 h-4" />
              <CreditCard className="w-5 h-5" />
              <ArrowRight className="w-4 h-4" />
              <Wallet className="w-5 h-5" />
              <RefreshCw className="w-4 h-4 ml-2 animate-spin" style={{ animationDuration: '2s' }} />
            </div>
          )}
          {cycle.type === 'poverty-loop' && (
            <div className="flex items-center gap-2 text-red-300">
              <AlertTriangle className="w-5 h-5" />
              <ArrowRight className="w-4 h-4" />
              <CreditCard className="w-5 h-5" />
              <ArrowRight className="w-4 h-4" />
              <TrendingDown className="w-5 h-5" />
              <ArrowRight className="w-4 h-4" />
              <Heart className="w-5 h-5" />
              <RefreshCw className="w-4 h-4 ml-2 animate-spin" style={{ animationDuration: '1.5s' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Advice Card Component
const AdviceCard = ({ cycle, t }) => {
  const [expanded, setExpanded] = useState(true);
  
  if (!cycle.advice) return null;

  return (
    <div 
      className="rounded-2xl overflow-hidden shadow-lg border"
      style={{ 
        backgroundColor: cycle.lightBg,
        borderColor: `${cycle.color}40`
      }}
    >
      <div 
        className="p-4 cursor-pointer flex items-center justify-between"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div 
            className="p-2 rounded-xl"
            style={{ backgroundColor: `${cycle.color}20` }}
          >
            <AlertCircle className="w-5 h-5" style={{ color: cycle.color }} />
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: cycle.color }}>
              {cycle.type === 'wealth-creation' ? t('moneyCycle.keepGrowing') : 
               cycle.type === 'day-to-day' ? t('moneyCycle.actionNeeded') : 
               t('moneyCycle.urgentAction')}
            </h3>
            <p className="text-sm text-gray-600">{t('moneyCycle.recommendations')}</p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </div>
      
      {expanded && (
        <div className="px-4 pb-4">
          <ul className="space-y-2">
            {cycle.advice.map((item, index) => (
              <li key={index} className="flex items-start gap-2">
                <CheckCircle 
                  className="w-4 h-4 mt-0.5 flex-shrink-0" 
                  style={{ color: cycle.color }} 
                />
                <span className="text-sm text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Animated Flow Arrow
const FlowArrow = ({ direction = "down", color = "#10B981" }) => (
  <div className="flex justify-center py-2">
    <div 
      className="p-2 rounded-full animate-bounce"
      style={{ backgroundColor: `${color}20` }}
    >
      {direction === "down" ? (
        <ArrowDown className="w-5 h-5" style={{ color }} />
      ) : (
        <ArrowRight className="w-5 h-5" style={{ color }} />
      )}
    </div>
  </div>
);

// Expandable Card Component
const ExpandableCard = ({ 
  title, 
  icon: Icon, 
  value, 
  subtitle,
  color = "#10B981",
  bgGradient,
  children,
  defaultExpanded = false,
  disabled = false
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div 
      className={`rounded-2xl overflow-hidden transition-all duration-300 shadow-lg ${disabled ? 'opacity-50' : ''}`}
      style={{ 
        background: bgGradient || `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`
      }}
    >
      <div 
        className={`p-4 ${children && !disabled ? 'cursor-pointer' : ''}`}
        onClick={() => children && !disabled && setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="p-3 rounded-xl"
              style={{ backgroundColor: `${color}20` }}
            >
              <Icon className="w-6 h-6" style={{ color }} />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">{title}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
              {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {children && !disabled && (
            <div className="p-2">
              {expanded ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          )}
        </div>
      </div>
      {children && expanded && !disabled && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// Health Indicator Badge
const HealthBadge = ({ status, label, value, t }) => {
  const configs = {
    good: { color: "#10B981", bg: "#D1FAE5", icon: CheckCircle, text: t('moneyCycle.healthy') },
    warning: { color: "#F59E0B", bg: "#FEF3C7", icon: AlertTriangle, text: t('moneyCycle.needsAttention') },
    danger: { color: "#EF4444", bg: "#FEE2E2", icon: AlertTriangle, text: t('moneyCycle.critical') }
  };
  const config = configs[status] || configs.warning;
  const Icon = config.icon;

  return (
    <div 
      className="flex items-center gap-2 px-3 py-2 rounded-full"
      style={{ backgroundColor: config.bg }}
    >
      <Icon className="w-4 h-4" style={{ color: config.color }} />
      <span className="text-sm font-medium" style={{ color: config.color }}>
        {label}: {value}
      </span>
    </div>
  );
};

// Mini Stat Card
const MiniStat = ({ label, value, icon: Icon, color = "#6B7280" }) => (
  <div className="flex items-center gap-2 p-3 bg-white rounded-xl shadow-sm">
    <Icon className="w-4 h-4" style={{ color }} />
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-semibold text-gray-700">{value}</p>
    </div>
  </div>
);

export const AlternativeMoneyCycleChart = ({
  currency = "USD",
  income = 0,
  expenses = 0,
  savingsMonthly = 0,
  passiveIncomeMonthly = 0,
  emergencyFundBalance = 0,
  totalDebtPaymentMonthly = 0,
  totalInvestments = 0,
  totalDebtBalance = 0,
  unproductiveAssets = 0,
  hasData = true,
}) => {
  const { t } = useTranslation();
  const surplus = income - expenses;
  const savingsRate = income > 0 ? (savingsMonthly / income) * 100 : 0;
  const emergencyMonths = expenses > 0 ? emergencyFundBalance / expenses : 0;
  const debtToIncomeRatio = income > 0 ? (totalDebtPaymentMonthly / income) * 100 : 0;
  
  // Get the financial cycle based on income vs expenses
  const cycle = getFinancialCycle(income, expenses, t);

  // Determine health statuses
  const getEmergencyStatus = () => {
    if (emergencyMonths >= 6) return "good";
    if (emergencyMonths >= 3) return "warning";
    return "danger";
  };

  const getSavingsStatus = () => {
    if (savingsRate >= 20) return "good";
    if (savingsRate >= 10) return "warning";
    return "danger";
  };

  const getDebtStatus = () => {
    if (debtToIncomeRatio <= 20) return "good";
    if (debtToIncomeRatio <= 35) return "warning";
    return "danger";
  };

  // Determine if user can save/invest based on their cycle
  const canSave = cycle.type === 'wealth-creation';
  const isVulnerable = cycle.type === 'day-to-day';
  const inDebtCycle = cycle.type === 'poverty-loop';

  return (
    <div className="space-y-6 pb-8">
      {/* Main Cycle Visualization */}
      <CycleVisualization 
        cycle={cycle}
        income={income}
        expenses={expenses}
        surplus={surplus}
      />

      {/* Advice Card */}
      <AdviceCard cycle={cycle} t={t} />

      {/* Money Flow Section - Only show for Wealth Creation */}
      {canSave && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800 px-1 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            {t('moneyCycle.wealthBuildingFlow')}
          </h3>
          
          {/* Savings Card */}
          <ExpandableCard
            title={t('moneyCycle.monthlySavings')}
            icon={PiggyBank}
            value={formatCurrency(savingsMonthly, currency)}
            subtitle={`${savingsRate.toFixed(1)}% ${t('moneyCycle.ofIncome')}`}
            color="#10B981"
          >
            <div className="grid grid-cols-2 gap-2">
              <MiniStat label={t('moneyCycle.target')} value="20%" icon={Target} color="#10B981" />
              <MiniStat label={t('moneyCycle.status')} value={savingsRate >= 20 ? t('moneyCycle.onTrack') : t('moneyCycle.belowTarget')} icon={BarChart3} color="#10B981" />
            </div>
          </ExpandableCard>

          <FlowArrow color="#10B981" />

          {/* Emergency Fund Card */}
          <ExpandableCard
            title={t('moneyCycle.emergencyFund')}
            icon={Shield}
            value={formatCurrency(emergencyFundBalance, currency)}
            subtitle={`${emergencyMonths.toFixed(1)} ${t('moneyCycle.monthsOfExpenses')}`}
            color="#3B82F6"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('moneyCycle.coverage')}</span>
                <HealthBadge 
                  status={getEmergencyStatus()} 
                  label={t('moneyCycle.months')} 
                  value={emergencyMonths.toFixed(1)}
                  t={t}
                />
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="h-2 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${Math.min(emergencyMonths / 6 * 100, 100)}%`,
                    backgroundColor: getEmergencyStatus() === 'good' ? '#10B981' : getEmergencyStatus() === 'warning' ? '#F59E0B' : '#EF4444'
                  }}
                />
              </div>
              <p className="text-xs text-gray-500">{t('moneyCycle.targetMonths')} ({formatCurrency(expenses * 6, currency)})</p>
            </div>
          </ExpandableCard>

          <FlowArrow color="#3B82F6" />

          {/* Investments Card */}
          <ExpandableCard
            title={t('moneyCycle.investments')}
            icon={BarChart3}
            value={formatCurrency(totalInvestments, currency)}
            subtitle={`${t('moneyCycle.generates')} ${formatCurrency(passiveIncomeMonthly, currency)}/${t('moneyCycle.mo')}`}
            color="#8B5CF6"
          >
            <div className="grid grid-cols-2 gap-2">
              <MiniStat label={t('moneyCycle.passiveIncome')} value={formatCurrency(passiveIncomeMonthly, currency)} icon={Zap} color="#8B5CF6" />
              <MiniStat label={t('moneyCycle.growth')} value={t('moneyCycle.building')} icon={TrendingUp} color="#8B5CF6" />
            </div>
          </ExpandableCard>
        </div>
      )}

      {/* Vulnerability Section - For Day-to-Day */}
      {isVulnerable && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800 px-1 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            {t('moneyCycle.vulnerabilityAssessment')}
          </h3>

          {/* Emergency Fund Status */}
          <ExpandableCard
            title={t('moneyCycle.emergencyFund')}
            icon={Shield}
            value={formatCurrency(emergencyFundBalance, currency)}
            subtitle={emergencyMonths < 1 ? t('moneyCycle.criticalLessThan1Month') : `${emergencyMonths.toFixed(1)} ${t('moneyCycle.monthsCoverage')}`}
            color="#F59E0B"
            defaultExpanded={true}
          >
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>{t('moneyCycle.risk')}:</strong> {t('moneyCycle.riskMessage')}
              </p>
            </div>
          </ExpandableCard>

          {/* Monthly Buffer */}
          <ExpandableCard
            title="Monthly Buffer"
            icon={Wallet}
            value={formatCurrency(surplus, currency)}
            subtitle="Available after expenses"
            color="#6B7280"
          >
            <p className="text-sm text-gray-600">
              Your current buffer is too small to build meaningful savings. Focus on reducing expenses or increasing income.
            </p>
          </ExpandableCard>
        </div>
      )}

      {/* Debt Cycle Section - For Poverty Loop */}
      {inDebtCycle && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800 px-1 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            Debt & Deficit Analysis
          </h3>

          {/* Monthly Deficit */}
          <ExpandableCard
            title="Monthly Deficit"
            icon={TrendingDown}
            value={formatCurrency(Math.abs(surplus), currency)}
            subtitle="Shortfall each month"
            color="#EF4444"
            defaultExpanded={true}
          >
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> You are spending {formatCurrency(Math.abs(surplus), currency)} more than you earn each month. This must come from debt or external support.
              </p>
            </div>
          </ExpandableCard>

          {/* Debt Status */}
          <ExpandableCard
            title="Total Debt"
            icon={CreditCard}
            value={formatCurrency(totalDebtBalance, currency)}
            subtitle={`${formatCurrency(totalDebtPaymentMonthly, currency)}/mo payments`}
            color="#991B1B"
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Debt-to-Income</span>
                <HealthBadge 
                  status={getDebtStatus()} 
                  label="Ratio" 
                  value={`${debtToIncomeRatio.toFixed(0)}%`}
                />
              </div>
              <p className="text-xs text-gray-500">
                Without addressing the deficit, debt will continue to grow.
              </p>
            </div>
          </ExpandableCard>

          {/* Support Needed */}
          <ExpandableCard
            title="External Support"
            icon={Heart}
            value="Required"
            subtitle="To cover the deficit"
            color="#EC4899"
          >
            <p className="text-sm text-gray-600">
              If you're not using debt, you may be relying on family, friends, or assistance programs. While helpful short-term, focus on becoming financially self-sufficient.
            </p>
          </ExpandableCard>
        </div>
      )}

      {/* Liabilities Section - Show for all cycles if debt exists */}
      {(totalDebtBalance > 0 || totalDebtPaymentMonthly > 0) && !inDebtCycle && (
        <div className="space-y-3 mt-6">
          <h3 className="text-lg font-semibold text-gray-800 px-1">Liabilities</h3>
          
          <ExpandableCard
            title="Total Debt"
            icon={CreditCard}
            value={formatCurrency(totalDebtBalance, currency)}
            subtitle={`${formatCurrency(totalDebtPaymentMonthly, currency)}/mo payments`}
            color="#EF4444"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Debt-to-Income</span>
                <HealthBadge 
                  status={getDebtStatus()} 
                  label="Ratio" 
                  value={`${debtToIncomeRatio.toFixed(0)}%`}
                />
              </div>
            </div>
          </ExpandableCard>
        </div>
      )}

      {/* Assets Section */}
      {unproductiveAssets > 0 && (
        <div className="space-y-3 mt-6">
          <h3 className="text-lg font-semibold text-gray-800 px-1">Other Assets</h3>
          
          <ExpandableCard
            title="Non-Income Assets"
            icon={Building}
            value={formatCurrency(unproductiveAssets, currency)}
            subtitle="Real estate, vehicles, etc."
            color="#6B7280"
          >
            <p className="text-xs text-gray-500">
              These assets have value but don't generate monthly income.
            </p>
          </ExpandableCard>
        </div>
      )}

      {/* Financial Health Summary */}
      <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Financial Health Summary
        </h3>
        <div className="flex flex-wrap gap-2">
          <HealthBadge 
            status={cycle.type === 'wealth-creation' ? 'good' : cycle.type === 'day-to-day' ? 'warning' : 'danger'} 
            label="Cycle" 
            value={cycle.name.split(' ')[0]}
          />
          {canSave && (
            <HealthBadge 
              status={getSavingsStatus()} 
              label="Savings Rate" 
              value={`${savingsRate.toFixed(0)}%`}
            />
          )}
          <HealthBadge 
            status={getEmergencyStatus()} 
            label="Emergency Fund" 
            value={`${emergencyMonths.toFixed(1)} mo`}
          />
          {(totalDebtBalance > 0 || inDebtCycle) && (
            <HealthBadge 
              status={getDebtStatus()} 
              label="Debt Ratio" 
              value={`${debtToIncomeRatio.toFixed(0)}%`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AlternativeMoneyCycleChart;
